// Pool registry — UI-shaped view over the canonical registry in
// `@tonado/core/deployments`. The core's deployments module is pure data +
// types (no Node imports), so importing it in the browser is safe and
// tree-shakes cleanly under Vite/esbuild.
//
// Drift previously crept in because this file maintained its own copy of
// the registry. Now there's a single source of truth; the deploy script's
// auto-patching of core's deployments.ts immediately reflects in the dApp
// after `pnpm --filter @tonado/core build`.

// `import * as` namespace form: more robust against Rollup's static-analysis
// quirks when consuming a CJS-compiled workspace package. Named-import form
// (`import { deployments } from ...`) fails the production build because
// Rollup can't statically see the `Object.defineProperty(exports, ...)`
// patterns that tsc generates for CJS.
import * as coreDeployments from "@tonado/core/deployments";
import type { PoolDeployment } from "@tonado/core/deployments";
import { NETWORK } from "./env";

const { deployments, isDeployed, poolKey } = coreDeployments;

/**
 * The dApp's flatter pool shape. We derive it from core's PoolDeployment
 * to keep the UI components agnostic of upstream-schema details.
 *
 * `asset` is a free-form asset id ("ton" for native, "jetton:EQ..." for
 * TEP-74 jettons). The picker filters to deployed pools only, so any value
 * present here is one the registry actually advertises.
 */
export type Pool = {
  key: string;
  asset: string;
  amount: string;
  denomination: string;
  address: string;
  /** Jetton-only: the TEP-74 master address. Undefined for native pools. */
  jettonMaster?: string;
  /** Jetton-only: the pool's own jetton wallet. Undefined for native pools. */
  poolJettonWallet?: string;
  /** Jetton-only: display symbol (e.g. "USDT"). Falls back to "JETTON" if absent. */
  jettonSymbol?: string;
  /** Jetton-only: TEP-74 decimals (for formatting). */
  jettonDecimals?: number;
};

function toDappPool(key: string, p: PoolDeployment): Pool {
  return {
    key,
    asset: p.asset,
    amount: p.amount,
    denomination: p.denomination,
    address: p.address,
    jettonMaster: p.jettonMaster,
    poolJettonWallet: p.poolJettonWallet,
    jettonSymbol: p.jettonSymbol,
    jettonDecimals: p.jettonDecimals,
  };
}

/**
 * Display label for a pool's asset. "TON" for native, the registry's
 * `jettonSymbol` for jettons (e.g. "USDT"), or a generic "JETTON" if
 * no symbol was recorded.
 */
export function assetUnitLabel(pool: Pool): string {
  if (pool.asset === "ton") return "TON";
  if (pool.jettonSymbol) return pool.jettonSymbol.toUpperCase();
  return "JETTON";
}

/**
 * Display label for a bare assetId string (e.g. from a parsed note).
 * Looks up the matching pool in the current network's registry to pick
 * the friendly symbol; falls back to a sensible default.
 */
export function assetIdLabel(assetId: string): string {
  if (assetId === "ton") return "TON";
  if (!assetId.startsWith("jetton:")) return assetId.toUpperCase();
  const slot = deployments[NETWORK]?.pools ?? {};
  for (const p of Object.values(slot)) {
    if (p.asset === assetId && p.jettonSymbol) {
      return p.jettonSymbol.toUpperCase();
    }
  }
  return "JETTON";
}

/**
 * Jetton metadata bundle, or null if the pool is native. Centralises
 * the jetton-path branching condition so each component doesn't redo
 * the `asset.startsWith("jetton:")` check.
 */
export function getJettonMeta(
  pool: Pool
): { master: string; symbol: string; decimals: number } | null {
  if (pool.asset === "ton") return null;
  if (!pool.jettonMaster) return null;
  return {
    master: pool.jettonMaster,
    symbol: assetUnitLabel(pool),
    decimals: pool.jettonDecimals ?? 9,
  };
}

/**
 * List every pool the registry advertises for the current network, filtered
 * to those that have actually been deployed (non-empty address). Undeployed
 * "placeholder" entries are hidden from the UI so users can't pick a tile
 * that resolves to a dead address.
 */
export function listPools(): Pool[] {
  const slot = deployments[NETWORK]?.pools ?? {};
  return Object.values(slot)
    .filter(isDeployed)
    .map((p) => toDappPool(poolKey(p.asset, p.amount), p))
    .sort((a, b) => {
      if (a.asset !== b.asset) return a.asset.localeCompare(b.asset);
      return Number(a.amount) - Number(b.amount);
    });
}

export function getPool(key: string): Pool | undefined {
  const p = deployments[NETWORK]?.pools[key];
  return p && isDeployed(p) ? toDappPool(key, p) : undefined;
}

/** Re-export the key helper so dApp components can build keys consistently. */
export { poolKey };
